import React, { useMemo } from 'react'
import { Grid, Card, CardContent, Typography, Stack, Box, LinearProgress, Button, Chip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useIsMobile } from '@/hooks/Responsive'

/**
 * BoardListCard
 * - variant="content" → 기존 썸네일 콘텐츠 카드
 * - variant="row" → 테이블 Row 기반 Card(수강정보/관리)
 */
export default function BoardListCard({
  rows = [],
  columns = [],
  getRowId,
  onRowClick,
  onTitleClick,
  onPreviewClick,
  gridCols = 3,
  variant = 'content',
  emptyMessage = '데이터가 없습니다.', 
  onDetailClick,
  onInterestClick,
}) {
  const navigate = useNavigate()
  const { isMobile } = useIsMobile()

  /* 공통 : 반응형 */
  const colMap = {
    1: { xs: 12 },
    2: { xs: 12, sm: 6 },
    3: { xs: 12, sm: 6, md: 4 },
    4: { xs: 12, sm: 6, md: 4, lg: 3 },
  }
  const gridProps = colMap[gridCols] || colMap[3]

  const getXRColor = (xr) => {
    if (xr === 'VL-PC') return 'secondary'
    if (xr === 'VL-MOB') return 'secondary2'
    if (xr === 'VL-ASYNC') return 'primary'
    if (xr === 'VL-HMD') return 'error'
    if (xr === 'VL-META') return 'error'
    return 'grey'
  }

  // 데모 데이터
      const imgArr = useMemo(() => ([
        { id: 1, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A004/A004_1.png' },
        { id: 2, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A003/A003_1.png' },
        { id: 3, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A005/A005_1.png' },
        { id: 4, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/B003/B003_1.png' },
        { id: 5, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/C001/C001_1.png' },
        { id: 6, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/C002/C002_1.png' },
      ]), [])
  /* -----------------------------
      1) variant = "content"
         기존 XR/콘텐츠 카드
  ----------------------------- */
  if (variant === 'content') {
    if (!rows || rows.length === 0) {
      return (
        <Box
          sx={{
            width: '100%',
            py: 10,
            textAlign: 'center',
            color: 'text.secondary',
            typography: 'body2',
          }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <ErrorOutlineIcon sx={{ fontSize: '32px', color: 'grey.light' }} />
            <Typography color="grey.500">{emptyMessage}</Typography>
          </Stack>
        </Box>
      )
    }
    return (
      <Grid container spacing={3}>
        {rows.map((row, index) => {
          const id = getRowId?.(row)

          return (
            <Grid key={id ?? `row-card-${index}`} item {...gridProps}>
              <Card
                elevation={0}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'hidden',
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s ease',
                }}
              >
                {/* 썸네일 */}
                <Box
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  {/* 기관 */}
                  {row.institution && (
                    <Chip
                      label="기관"
                      color="grey"
                      variant="contained"
                      sx={{
                        position: 'absolute',
                        top: '16px',
                        left: '16px',
                        typography: 'body1',
                        fontWeight: '600',
                        zIndex: 2,
                      }}
                    />
                  )}
                  {row.thumbUrl && row.thumbUrl.trim() !== '' ? (
                    <img
                      src={row.thumbUrl}
                      alt=""
                      style={{
                        width: '100%',
                        aspectRatio: '16/9',
                        objectFit: 'cover',
                        transition: 'transform 0.4s ease',
                      }}
                    />
                  ) : (
                    <img
                      src="/assets/images/No image.png"
                      //임시 이미지 적용(2026-01-14)
                      // src={imgArr[index].thumbUrl}
                      alt="이미지 없음"
                      style={{
                        width: '100%',
                        aspectRatio: '16/9',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                </Box>

                {/* 콘텐츠 */}
                <CardContent
                  sx={{
                    p: 2,
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  {/* XR 타입 + 좋아요 */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {(row.xr) && (
                      <Chip
                        variant="outlined"
                        color={getXRColor(row.xr)}
                        label={row.xr}
                        sx={{ minWidth: '64px' }}
                      />
                    )}

                    {row.interest !== null && (
                      <FavoriteIcon
                        onClick={(e) => {
                        e.stopPropagation()
                        onInterestClick?.(row)
                      }}
                        sx={{
                          color: row.interest ? 'primary.main' : 'divider',
                          ml: 'auto',
                          cursor: 'pointer',
                        }}
                      />
                    )}
                  </Stack>

                  {/* 난이도 / 강좌 갯수 */}
                  {(row.lectureLevel || row.sessionTotal) && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                    >
                      <Chip
                        variant="outlined"
                        color="grey"
                        label={row.lectureLevel}
                        sx={{ minWidth: '42px' }}
                      />

                      <Typography fontWeight={600} variant="body2">
                        총 {row.sessionTotal}강
                      </Typography>
                    </Stack>
                  )}

                  {/* 제목 */}
                  <Typography
                    variant={isMobile ? 'subtitle2' : 'subtitle1'}
                    fontWeight={400}
                    sx={{
                      my: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      wordBreak: 'keep-all',
                    }}
                    title={row.title}
                  >
                    {row.title}
                  </Typography>

                  {/* 카테고리 */}
                  {(row.Cat || row.category) && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 3,
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        WebkitLineClamp: 1,
                        maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : 'auto',
                      }}
                    >
                      {[row.Cat, row.subCat, row.detailCat].filter(Boolean).join(' > ')}
                      {row.category}
                    </Typography>
                  )}

                  {/* 해시태그 */}
                  {Array.isArray(row.hashtag) && row.hashtag.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        mb: 3,
                        flexWrap: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row.hashtag.map((tag, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                        >
                          {tag}
                        </Typography>
                      ))}
                    </Stack>
                  )}

                  {/* 버튼 영역 */}
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 'auto' }}
                  >
                    {row.preview && (
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        size="medium"
                        onClick={(e) => {
                          e.stopPropagation()
                          onPreviewClick?.(row)
                        }}
                      >
                        미리보기
                      </Button>
                    )}

                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (row.href) {
                          // 외부 또는 내부 링크 이동
                          if (row.href.startsWith('http')) {
                            window.open(row.href, '_blank', 'noopener noreferrer')
                          } else {
                            navigate(row.href)
                          }
                        } else {
                          onDetailClick?.(row)
                        }
                      }}
                    >
                      상세보기
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>
    )
  }

  /* -----------------------------
      2) variant = "row"
         테이블 Row 기반 CardView
  ----------------------------- */

  const titleCol =
    columns.find((c) => c.isTitle && !c.hideInCard) ??
    columns.find((c) => !c.hideInCard)

  const subCols = columns.filter(
    (c) => c !== titleCol && !c.hideInCard
  )

  if (!rows || rows.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          py: 10,
          textAlign: 'center',
          color: 'text.secondary',
          typography: 'body2',
        }}
      >
        <Stack alignItems="center" spacing={1.5}>
          <ErrorOutlineIcon sx={{ fontSize: '32px', color: 'grey.light' }} />
          <Typography color="grey.500">{emptyMessage}</Typography>
        </Stack>
      </Box>
    )
  }
  
  return (
    <Grid container spacing={3}>
      {rows.map((row, index) => {
        const id = getRowId?.(row)

        return (
          <Grid key={id ?? `row-card-${index}`} item {...gridProps}>
            <Card
              elevation={0}
              onClick={() =>
                onTitleClick ? onTitleClick(row) : onRowClick?.(row)
              }
              sx={{
                px: 2,
                py: 3,
                height: '100%',
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* 타이틀 */}
              <Typography component="div" sx={{ mb: 1 }}>
                {titleCol.render
                  ? titleCol.render(row[titleCol.field], row)
                  : row[titleCol.field]}
              </Typography>

              {/* 서브 */}
              <Stack spacing={1} sx={{ flexGrow: 1 }}>
                {subCols.map((c) => {
                  const rawValue = row[c.field]
                  const value = c.render ? c.render(rawValue, row) : rawValue

                  // progressRate
                  if (c.field.includes('progressRate')) {
                    const percent = (() => {
                      const session = Number(row.session)
                      const total = Number(row.totalSession)

                      // session / totalsession
                      if (Number.isFinite(session) && Number.isFinite(total) && total > 0) {
                        return Math.min(
                          Math.round((session / total) * 100),
                          100
                        )
                      }

                      // session 없을 경우
                      const raw = Number(row[c.field] ?? row.progressRatePer)

                      if (Number.isFinite(raw)) {
                        return Math.min(Math.round(raw), 100)
                      }

                    })()

                    return (
                      <Stack
                        key={c.field}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ width: '100%' }}
                      >
                        {!c.hideTitleInCard && c.headerName && (
                          <Typography fontWeight={500} color="text.secondary">
                            {c.headerName}
                          </Typography>
                        )}

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ flex: 1 }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={percent}
                            sx={{
                              width: '100%',
                              height: '8px',
                              borderRadius: 1,
                              bgcolor: 'grey.100',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ flexShrink: 0 }}
                          >
                            {percent}%
                          </Typography>
                        </Stack>
                      </Stack>
                    )
                  }

                  /* 기본 항목 */
                  return (
                    <Typography
                      key={c.field}
                      variant="body1"
                    >
                      {!c.hideTitleInCard && c.headerName
                        ? `${c.headerName} `
                        : ''}
                      {value}
                    </Typography>
                  )
                })}
              </Stack>
            </Card>
          </Grid>
        )
      })}
    </Grid>
  )
}
